/**
 * The full-width sticky banner under the topbar — the CRITICAL surface
 * (Flow F Frame 4; ADR-029 §5).
 *
 * # One banner at a time
 *
 * Flow F §Behavior notes: *"Sticky banners. One at a time. If multiple
 * critical conditions are active, the highest-priority one is shown; a
 * '+2 more' pill indicates additional conditions, and clicking it expands
 * a list."* ADR-029 §Consequences says the same, ordered by recency. The
 * store keeps every critical (uncapped — a CRITICAL condition is never
 * discarded); this component shows ``banners[0]`` and collapses the rest
 * behind an expander.
 *
 * # No focus trap — a ratified departure from the storyboard
 *
 * Flow F's accessibility notes ask for focus to be trapped on the banner's
 * action when it first appears. That is **deliberately not implemented**
 * (feature-spec §0.1 D3, architect-ratified 2026-07-26): trapping keyboard
 * focus in a **non-modal** surface is a WCAG 2.1.2 (No Keyboard Trap)
 * hazard, and a persistent CRITICAL banner would hold focus indefinitely —
 * an operator mid-task in the map or a form would be yanked out of it by an
 * arriving alert and unable to get back.
 *
 * ``role="alert"`` already forces an assertive announcement without
 * stealing focus. The banner is mounted **first in DOM order after the
 * topbar**, so it is the next thing a keyboard user tabs into rather than
 * something they must hunt for. Announcement without abduction.
 *
 * # Close vs. Dismiss
 *
 * As with toasts, ``×`` is local-only and a SAPIENT alert's system-wide
 * dismissal is an explicit labelled action. Note that Flow F's F.5
 * *per-user "Acknowledge" with a 5-minute reappear* belongs to
 * Meridian-originated system banners, which are **out of scope** here
 * (feature-spec §0.1 D5) — ADR-029 §6.2 is explicit that an alert's copy
 * says **Dismiss**, and the two semantics are not interchangeable even
 * though they share this surface.
 *
 * Cross-references:
 *   - storyboard Flow F Frame 4 + §Behavior notes + §Accessibility.
 *   - ADR-029 §5 (CRITICAL → banner) / §6.2 (Dismiss copy) / §7 (scope).
 *   - REQ-UI-046; REQ-UI-059.
 *   - feature-specs/alert-notification-surface/feature-spec.md §0.1 D3.
 */
import { CaretDown, CaretUp, X } from "@phosphor-icons/react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { severityChrome } from "@/notifications/notification-chrome";
import { useActiveBanner, useNotificationStore } from "@/notifications/notification-store";

/**
 * Render the active CRITICAL banner, if any, with a "+N more" expander for
 * additional live critical conditions. Renders nothing when none is active.
 */
export function BannerHost(): React.ReactElement | null {
  const { active, overflowCount } = useActiveBanner();
  const banners = useNotificationStore((s) => s.banners);
  const close = useNotificationStore((s) => s.close);
  const [expanded, setExpanded] = useState(false);

  if (active === null) {
    return null;
  }

  const { Glyph, srPrefix, accentClass } = severityChrome(active.severity);

  return (
    <div
      role="alert"
      data-testid="banner-host"
      data-severity={active.severity}
      className={cn("w-full border-b border-destructive/40 bg-destructive/10 px-4 py-2")}
    >
      <div className="flex items-start gap-3">
        <Glyph aria-hidden className={cn("mt-0.5 shrink-0", accentClass)} size={20} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-text-primary">
            <span className="sr-only">{srPrefix}: </span>
            {active.title}
          </p>
          {active.body !== undefined && active.body !== "" && (
            <p className="mt-0.5 text-xs text-text-secondary">{active.body}</p>
          )}
          {active.actions !== undefined && active.actions.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-3">
              {active.actions.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  onClick={action.onSelect}
                  className={cn(
                    "rounded text-xs font-medium underline-offset-2 hover:underline",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    action.systemWide === true ? "text-destructive" : "text-brand",
                  )}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
          {overflowCount > 0 && (
            <>
              <button
                type="button"
                onClick={() => setExpanded((prior) => !prior)}
                aria-expanded={expanded}
                className={cn(
                  "mt-1.5 inline-flex items-center gap-1 rounded text-xs font-medium",
                  "text-text-secondary hover:text-text-primary",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                )}
              >
                +{overflowCount} more
                {expanded ? <CaretUp aria-hidden size={12} /> : <CaretDown aria-hidden size={12} />}
              </button>
              {expanded && (
                <ul className="mt-1.5 space-y-1 border-t border-border pt-1.5">
                  {banners.slice(1).map((backlogged) => (
                    <li key={backlogged.id} className="text-xs text-text-secondary">
                      {backlogged.title}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => close(active.id)}
          aria-label="Close notification"
          className={cn(
            "shrink-0 rounded p-1 text-text-muted hover:text-text-primary",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          <X aria-hidden size={14} />
        </button>
      </div>
    </div>
  );
}
